<template>
  <div v-if="messages.length > 0" class="social-proof-container">
    <div
      v-for="(msg, index) in messages"
      :key="index"
      class="social-proof-badge"
      :class="`social-proof-badge--${msg.type}`"
    >
      <span class="social-proof-badge__icon" v-if="msg.icon">
        <!-- Flame Emoji / Animated styled icon -->
        <span v-if="msg.icon === 'fire'" class="emoji-icon">🔥</span>
        
        <!-- Shopping Cart with stroke outline -->
        <svg
          v-else-if="msg.icon === 'cart'"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 3H5.2L7.3 14.2C7.4 14.8 7.9 15.3 8.5 15.3H18.5C19.1 15.3 19.6 14.8 19.7 14.2L21 7H6"
            stroke="#333333"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <line x1="9" y1="10" x2="18" y2="10" stroke="#333333" stroke-width="1.8" stroke-linecap="round"/>
          <line x1="10" y1="13" x2="17" y2="13" stroke="#333333" stroke-width="1.8" stroke-linecap="round"/>
          <circle cx="9" cy="19" r="1.8" fill="#333333"/>
          <circle cx="18" cy="19" r="1.8" fill="#333333"/>
        </svg>

        <!-- Star Icon with outline -->
        <svg
          v-else-if="msg.icon === 'star'"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="#FFD13B"
          stroke="#222222"
          stroke-width="1.8"
          stroke-linejoin="round"
          xmlns="http://www.w3.org/2000/svg"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      </span>
      
      <span class="social-proof-badge__text">
        <template v-if="msg.type === 'purchase'">
          <strong>128 people</strong> bought this week
        </template>
        <template v-else-if="msg.type === 'cart'">
          In <strong>23 carts</strong> right now
        </template>
        <template v-else-if="msg.type === 'authority'">
          <strong>Best seller</strong> in kid gifts
        </template>
        <template v-else>
          {{ msg.text }}
        </template>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { SocialProofVariantKey } from '@/composables/abtest/contexts/abtestContextSocialProof'

interface Props {
  variant?: string
}

const props = defineProps<Props>()

interface SocialProofMessage {
  type: string
  icon: string
  text: string
}

const messages = computed<SocialProofMessage[]>(() => {
  switch (props.variant) {
    case SocialProofVariantKey.Purchase:
      return [
        {
          type: 'purchase',
          icon: 'fire',
          text: '128 people bought this week',
        },
      ]
    case SocialProofVariantKey.Cart:
      return [
        {
          type: 'cart',
          icon: 'cart',
          text: 'In 23 carts right now',
        },
      ]
    case SocialProofVariantKey.Authority:
      return [
        {
          type: 'authority',
          icon: 'star',
          text: 'Best seller in kid gifts',
        },
      ]
    case SocialProofVariantKey.Combo:
      return [
        {
          type: 'purchase',
          icon: 'fire',
          text: '128 bought this week',
        },
        {
          type: 'cart',
          icon: 'cart',
          text: 'In 23 carts right now',
        },
      ]
    default:
      return []
  }
})
</script>

<style scoped lang="scss">
.social-proof-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
  width: 100%;
}

.social-proof-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: var(--border-radius-medium);
  font-size: 1rem;
  line-height: 1.4;
  width: 100%;

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
  }

  .emoji-icon {
    font-size: 18px;
    line-height: 1;
  }

  &--purchase {
    background-color: #f0f7ff;
    color: #2b5687;

    strong {
      color: #1e3a60;
      font-weight: 700;
    }
  }

  &--cart {
    background-color: #fff9ed;
    color: #855b14;

    strong {
      color: #634106;
      font-weight: 700;
    }
  }

  &--authority {
    background-color: #f0fdf4;
    color: #276749;

    strong {
      color: #1a4931;
      font-weight: 700;
    }
  }
}
</style>
